// Mobile port of the web Task Configuration → Groups editor. Reads/writes
// user_settings.groups directly (see src/interfaces/settings.ts Group on
// web) — same shape, same source of truth as the web app.
import React from 'react';
import { Alert, Pressable, StyleSheet, Text, View } from 'react-native';
import { ThemedSwitch } from './ThemedSwitch';
import { makeThemedStyles } from '../../v10/theme';
import { SectionTitle, Field, GoldButton, GlassCard } from '../../v10/ui';
import { useSettingColumn } from './useSettingColumn';

interface TimeBlock {
  id: string;
  day: string;
  startTime: string;
  endTime: string;
}

interface Group {
  id: string;
  isActive: boolean;
  title: string;
  groupNumber: number;
  parentId: string | null;
  scheduleActive: boolean;
  scheduleTimeBlocks: TimeBlock[];
  outlineColor?: string;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function nextDay(day: string): string {
  const idx = DAYS.indexOf(day);
  return DAYS[(idx + 1) % DAYS.length];
}

export default function GroupsEditor({ userId }: { userId: string | undefined }) {
  const styles = useStyles();
  const { value: groups, setValue: setGroups, loading } = useSettingColumn<Group[]>(userId, 'groups', []);

  const updateGroup = (id: string, patch: Partial<Group>) => {
    setGroups(groups.map((g) => (g.id === id ? { ...g, ...patch } : g)));
  };

  const deleteGroup = (id: string) => {
    Alert.alert('Delete group?', 'This cannot be undone.', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: () => setGroups(groups.filter((g) => g.id !== id)),
      },
    ]);
  };

  const addGroup = () => {
    const newGroup: Group = {
      id: `group-${Date.now()}`,
      isActive: true,
      title: 'New Group',
      groupNumber: groups.length + 1,
      parentId: null,
      scheduleActive: false,
      scheduleTimeBlocks: [],
      outlineColor: '#767c86',
    };
    setGroups([...groups, newGroup]);
  };

  const addTimeBlock = (group: Group) => {
    const block: TimeBlock = {
      id: `block-${Date.now()}`,
      day: 'Mon',
      startTime: '09:00',
      endTime: '10:00',
    };
    updateGroup(group.id, { scheduleTimeBlocks: [...group.scheduleTimeBlocks, block] });
  };

  const updateTimeBlock = (group: Group, blockId: string, patch: Partial<TimeBlock>) => {
    updateGroup(group.id, {
      scheduleTimeBlocks: group.scheduleTimeBlocks.map((b) => (b.id === blockId ? { ...b, ...patch } : b)),
    });
  };

  const deleteTimeBlock = (group: Group, blockId: string) => {
    updateGroup(group.id, {
      scheduleTimeBlocks: group.scheduleTimeBlocks.filter((b) => b.id !== blockId),
    });
  };

  const cycleParent = (group: Group) => {
    const options: (string | null)[] = [null, ...groups.filter((g) => g.id !== group.id).map((g) => g.id)];
    const idx = options.indexOf(group.parentId);
    const nextIdx = (idx + 1) % options.length;
    updateGroup(group.id, { parentId: options[nextIdx] });
  };

  const parentLabel = (group: Group) => {
    if (!group.parentId) return 'None';
    const parent = groups.find((g) => g.id === group.parentId);
    return parent ? parent.title : 'Unknown';
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <Text style={styles.hint}>Loading…</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <SectionTitle style={styles.sectionGap}>Task Groups</SectionTitle>
      {groups.length === 0 ? <Text style={styles.hint}>No groups yet.</Text> : null}

      {groups.map((group) => (
        <GlassCard key={group.id} style={styles.card}>
          <View style={styles.row}>
            <Field style={styles.flex1}
              inputStyle={[styles.input, styles.flex1]}
              value={group.title}
              onChangeText={(t) => updateGroup(group.id, { title: t })}
              placeholder="Group title"
            />
            <ThemedSwitch value={group.isActive} onValueChange={(v) => updateGroup(group.id, { isActive: v })} />
          </View>

          <View style={styles.row}>
            <Text style={styles.label}>Group #</Text>
            <Field
              inputStyle={[styles.input, styles.numInput]}
              value={String(group.groupNumber)}
              onChangeText={(t) => updateGroup(group.id, { groupNumber: parseInt(t, 10) || 0 })}
              keyboardType="numeric"
            />
            <Text style={styles.label}>Outline</Text>
            <Field
              inputStyle={[styles.input, styles.numInput]}
              value={group.outlineColor ?? ''}
              onChangeText={(t) => updateGroup(group.id, { outlineColor: t })}
              placeholder="#767c86"
              autoCapitalize="none"
            />
          </View>

          <View style={styles.row}>
            <Text style={styles.label}>Parent</Text>
            <Pressable style={styles.pickerBtn} onPress={() => cycleParent(group)}>
              <Text style={styles.pickerText}>{parentLabel(group)}</Text>
            </Pressable>
          </View>

          <View style={styles.row}>
            <Text style={styles.label}>Scheduled</Text>
            <ThemedSwitch
              value={group.scheduleActive}
              onValueChange={(v) => updateGroup(group.id, { scheduleActive: v })}
            />
          </View>

          {group.scheduleActive ? (
            <GlassCard style={styles.subCard}>
              {group.scheduleTimeBlocks.map((block) => (
                <View key={block.id} style={styles.row}>
                  <Pressable
                    style={styles.pickerBtn}
                    onPress={() => updateTimeBlock(group, block.id, { day: nextDay(block.day) })}
                  >
                    <Text style={styles.pickerText}>{block.day}</Text>
                  </Pressable>
                  <Field
                    inputStyle={[styles.input, styles.timeInput]}
                    value={block.startTime}
                    onChangeText={(t) => updateTimeBlock(group, block.id, { startTime: t })}
                    placeholder="09:00"
                  />
                  <Text style={styles.label}>–</Text>
                  <Field
                    inputStyle={[styles.input, styles.timeInput]}
                    value={block.endTime}
                    onChangeText={(t) => updateTimeBlock(group, block.id, { endTime: t })}
                    placeholder="10:00"
                  />
                  <Pressable style={styles.deleteBtn} onPress={() => deleteTimeBlock(group, block.id)}>
                    <Text style={styles.deleteText}>✕</Text>
                  </Pressable>
                </View>
              ))}
              <GoldButton label="+ Add Time Block" onPress={() => addTimeBlock(group)} style={styles.actionGap} />
            </GlassCard>
          ) : null}

          <Pressable style={styles.dangerButton} onPress={() => deleteGroup(group.id)}>
            <Text style={styles.dangerText}>Delete Group</Text>
          </Pressable>
        </GlassCard>
      ))}

      <GoldButton label="+ Add New Group" onPress={addGroup} style={styles.actionGap} />
    </View>
  );
}

const useStyles = makeThemedStyles(({ t, radii }) =>
  StyleSheet.create({
  actionGap: { marginTop: 8 },
  sectionGap: { marginTop: 18 },
  container: { gap: 8 },
  hint: { fontSize: 12, color: t.ivorySoft, marginTop: 6, fontStyle: 'italic' },
  card: {
    padding: 12,
    marginTop: 10,
    gap: 8,
  },
  subCard: {
    padding: 10,
    gap: 8,
  },
  row: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  flex1: { flex: 1 },
  label: { fontSize: 12, color: t.ivorySoft, fontWeight: '700' },
  input: {
  },
  numInput: { width: 90 },
  timeInput: { width: 70 },
  pickerBtn: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  pickerText: { fontSize: 13, fontWeight: '700', color: t.goldBright },
  deleteBtn: {
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    backgroundColor: t.glass,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  deleteText: { color: t.lapsed, fontWeight: '900' },
  dangerButton: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingVertical: 10,
    alignItems: 'center',
    marginTop: 4,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  dangerText: { color: t.lapsed, fontWeight: '800' },
}),
);
